import axios from "axios";
import React, { useContext, useEffect, useMemo, useState } from "react";
import Client from "client-sdk/src/client";
import { GetAllPartnersRequestBuilder } from "client-sdk/src/partners/partners";
import {
  GetAllOrderTypesRequestBuilder,
  GetAllDateTypesRequestBuilder,
  GetStatusRequestBuilder,
  filter,
  resetFilter as resetFilterApi,
  GetAllStatisticByOrderTypeRequestBuilder,
  GetAllStatisticByDeadlineRequestBuilder,
} from "client-sdk/src/operator-assingment/operator-assingment";

import { Status } from "client-sdk/src/operator-assingment/interface";

type Props = {
  children: JSX.Element;
};

type operatorAssignmentContextType = {
  orderTypes: any[];
  dateTypes: any[];
  status: any;
  partners: any[];
  saveFilter: (data: Status) => Promise<void>;
  resetFilter: () => void;
  getStatisticByOrderType: () => void;
  getStatisticByDeadline: () => void;
  statistic: any;
  lastRefresh: number;
  refreshing: boolean;
};

export const request = axios.create({
  baseURL: "https://api.qa.ronad.ir/api",
});

request.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

const OperatorAssignmentContext = React.createContext({});

export function useOperatorAssignmentContext(): operatorAssignmentContextType {
  //@ts-ignore
  return useContext(OperatorAssignmentContext);
}

export default function ContextProvider({ children }: Props) {
  const [orderTypes, setOrderTypes] = useState([]);
  const [dateTypes, setDateTypes] = useState([]);
  const [status, setStatus] = useState({});
  const [partners, setPartners] = useState([]);
  const [statistic, setStatistic] = useState({});
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [refreshing, setRefreshing] = useState(false);

  const client = useMemo(() => {
    return new Client(localStorage.getItem("token") || "");
  }, []);

  useEffect(() => {
    getOrderTypes();
    getDateTypes();
    getStatus();
    getPartners();
  }, []);

  function getOrderTypes() {
    client
      .execute(new GetAllOrderTypesRequestBuilder().build())
      .then(({ data }) => setOrderTypes(data));
  }

  function getPartners() {
    client
      .execute(new GetAllPartnersRequestBuilder().params({size: 1000}).build())
      .then(({ data }) => setPartners(data));
  }

  function getStatisticByOrderType() {
    setRefreshing(true);
    client
      .execute(new GetAllStatisticByOrderTypeRequestBuilder().build())
      .then(({ data }) => {
        setStatistic(data);
        setLastRefresh(Date.now());
        setRefreshing(false);
      });
  }

  function getStatisticByDeadline() {
    setRefreshing(true);
    client
      .execute(new GetAllStatisticByDeadlineRequestBuilder().build())
      .then(({ data }) => {
        setStatistic(data);
        setLastRefresh(Date.now());
        setRefreshing(false);
      });
  }

  function getDateTypes() {
    client
      .execute(new GetAllDateTypesRequestBuilder().build())
      .then(({ data }) => setDateTypes(data));
  }

  function getStatus() {
    client
      .execute(new GetStatusRequestBuilder().build())
      .then(({ data }) => setStatus(data));
  }

  function saveFilter(data: Status) {
    return client
      .execute(new filter().data(data).build())
      .then(({ data }) => setStatus(data));
  }

  function resetFilter() {
    client
      .execute(new resetFilterApi().build())
      .then(({ data }) => setStatus(data));
  }

  return (
    <OperatorAssignmentContext.Provider
      value={{
        orderTypes,
        dateTypes,
        status,
        partners,
        saveFilter,
        resetFilter,
        getStatisticByOrderType,
        getStatisticByDeadline,
        statistic,
        lastRefresh,
        refreshing,
      }}
    >
      {children}
    </OperatorAssignmentContext.Provider>
  );
}
